import { notify } from "@general-backoffice/core";
import CardHeader from "@general-backoffice/core/lib/components/card/CardHeader";
import type { TaxProfile } from "@investhinks/api-client";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardBody, Spinner, UncontrolledTooltip } from "reactstrap";

/**
 * VIES Verification Card for EU Business referrers.
 * Shows verification status and allows triggering re-verification.
 */
type VIESTaxProfile = Pick<TaxProfile, "taxIdNumber" | "viesVerified" | "viesVerifiedAt" | "viesName" | "viesAddress">;

interface VIESVerificationProps {
  taxProfile: VIESTaxProfile | null;
  onVerify: (vatNumber: string) => Promise<void>;
  isLoading?: boolean;
}

const VIESVerification = ({ taxProfile, onVerify, isLoading: externalLoading }: VIESVerificationProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.vies" });
  const [isVerifying, setIsVerifying] = useState(false);

  const { taxIdNumber, viesVerified, viesVerifiedAt, viesName, viesAddress } = taxProfile || {};

  const isLoading = externalLoading || isVerifying;

  // Calculate validity status
  const validityInfo = getValidityInfo(viesVerifiedAt);

  const handleVerify = useCallback(async () => {
    if (!taxIdNumber) {
      notify.error(t("errors.noVatNumber", "No VAT number provided"));
      return;
    }

    setIsVerifying(true);
    try {
      await onVerify(taxIdNumber);
      notify.success(t("success.verified", "VAT number verified successfully"));
    } catch (error) {
      notify.error(
        (error instanceof Error ? error.message : null) || t("errors.verificationFailed", "Verification failed")
      );
    } finally {
      setIsVerifying(false);
    }
  }, [taxIdNumber, onVerify, t]);

  return (
    <Card className="shadow-sm">
      <CardHeader
        title={t("title", "VIES Verification")}
        subtitle={taxIdNumber || t("noVatNumber", "No VAT number")}
        isLoading={isLoading}
      />
      <CardBody>
        {/* Status Badge */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="text-muted">{t("status", "Status")}:</span>
          <VIESStatusBadge viesVerified={viesVerified} validityInfo={validityInfo} />
        </div>

        {/* Last Verified */}
        {viesVerifiedAt && (
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted">{t("lastVerified", "Last Verified")}:</span>
            <span>
              {new Date(viesVerifiedAt).toLocaleDateString()}{" "}
              <small className="text-muted">
                (
                {validityInfo.daysAgo === 0
                  ? t("today", "today")
                  : `${validityInfo.daysAgo} ${t("daysAgo", "days ago")}`}
                )
              </small>
            </span>
          </div>
        )}

        {/* VIES Company Name */}
        {viesName && (
          <div className="mb-3">
            <span className="text-muted d-block mb-1">{t("companyName", "Company Name")}:</span>
            <span className="font-weight-bold">{viesName}</span>
          </div>
        )}

        {/* VIES Address */}
        {viesAddress && (
          <div className="mb-3">
            <span className="text-muted d-block mb-1">{t("address", "Address")}:</span>
            <span className="text-pre-wrap">{viesAddress}</span>
          </div>
        )}

        {/* Validity Warning */}
        {validityInfo.isInGracePeriod && (
          <div className="alert alert-warning mb-3">
            <i className="fas fa-exclamation-triangle mr-2" />
            {t("gracePeriodWarning", "VIES verification is in grace period. Please re-verify soon.")}
          </div>
        )}

        {validityInfo.isExpired && (
          <div className="alert alert-danger mb-3">
            <i className="fas fa-times-circle mr-2" />
            {t("expiredWarning", "VIES verification has expired. Re-verification required.")}
          </div>
        )}

        {/* Verify Button - only show if not verified, expired, or in grace period */}
        {(!viesVerified || validityInfo.isExpired || validityInfo.isInGracePeriod) && (
          <Button
            color={validityInfo.isExpired ? "danger" : "primary"}
            onClick={handleVerify}
            disabled={isLoading || !taxIdNumber}
            block
          >
            {isVerifying ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("verifying", "Verifying...")}
              </>
            ) : viesVerified ? (
              <>
                <i className="fas fa-sync-alt mr-2" />
                {t("reVerify", "Re-verify")}
              </>
            ) : (
              <>
                <i className="fas fa-check-circle mr-2" />
                {t("verify", "Verify VAT Number")}
              </>
            )}
          </Button>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Calculate validity information from verification date.
 */
interface ValidityInfo {
  isValid: boolean;
  isExpired: boolean;
  isInGracePeriod: boolean;
  daysAgo: number | null;
  daysRemaining: number | null;
}

const getValidityInfo = (viesVerifiedAt: string | null | undefined): ValidityInfo => {
  if (!viesVerifiedAt) {
    return {
      isValid: false,
      isExpired: false,
      isInGracePeriod: false,
      daysAgo: null,
      daysRemaining: null,
    };
  }

  const verifiedDate = new Date(viesVerifiedAt);
  const now = new Date();
  const diffMs = now.getTime() - verifiedDate.getTime();
  const daysAgo = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const VALIDITY_DAYS = 90;
  const GRACE_PERIOD_DAYS = 7;
  const daysRemaining = VALIDITY_DAYS - daysAgo;

  return {
    isValid: daysAgo <= VALIDITY_DAYS + GRACE_PERIOD_DAYS,
    isExpired: daysAgo > VALIDITY_DAYS + GRACE_PERIOD_DAYS,
    isInGracePeriod: daysAgo > VALIDITY_DAYS && daysAgo <= VALIDITY_DAYS + GRACE_PERIOD_DAYS,
    daysAgo,
    daysRemaining: Math.max(0, daysRemaining),
  };
};

/**
 * VIES Status Badge component.
 */
interface VIESStatusBadgeProps {
  viesVerified: boolean | null | undefined;
  validityInfo: ValidityInfo;
}

const VIESStatusBadge = ({ viesVerified, validityInfo }: VIESStatusBadgeProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.vies" });

  if (!viesVerified) {
    return (
      <Badge color="secondary" pill>
        <i className="fas fa-question-circle mr-1" />
        {t("statusNotVerified", "Not Verified")}
      </Badge>
    );
  }

  if (validityInfo.isExpired) {
    return (
      <Badge color="danger" pill>
        <i className="fas fa-times-circle mr-1" />
        {t("statusExpired", "Expired")}
      </Badge>
    );
  }

  if (validityInfo.isInGracePeriod) {
    return (
      <>
        <Badge color="warning" pill id="vies-grace-badge">
          <i className="fas fa-clock mr-1" />
          {t("statusGracePeriod", "Grace Period")}
        </Badge>
        <UncontrolledTooltip target="vies-grace-badge" placement="top">
          {t("gracePeriodTooltip", "{{days}} days remaining", {
            days: 7 - ((validityInfo.daysAgo ?? 0) - 90),
          })}
        </UncontrolledTooltip>
      </>
    );
  }

  return (
    <>
      <Badge color="success" pill id="vies-valid-badge">
        <i className="fas fa-check-circle mr-1" />
        {t("statusValid", "Valid")}
      </Badge>
      <UncontrolledTooltip target="vies-valid-badge" placement="top">
        {t("validTooltip", "{{days}} days remaining", {
          days: validityInfo.daysRemaining,
        })}
      </UncontrolledTooltip>
    </>
  );
};

export default VIESVerification;
